import React, { lazy } from 'react';
import Contact from 'App/resources/Model/Contact';
import Client from 'App/resources/Model/Client';
import { UserDefinedObjectTypeId } from 'App/resources/Model/UserDefinedObjectType';
import { CardEntity } from 'App/pages/shared/CardContentPages/types';
import { hasSoftDeployAccess, isAvailableProduct, Product } from 'App/services/FeatureHelperStandalone';
import config from 'App/babel/config';

const Overview = lazy(() => import('App/pages/ClientCard/ContentPages/Overview'));
const SharedActivities = lazy(() => import('App/pages/shared/CardContentPages/Activities'));
const SharedOpportunities = lazy(() => import('App/pages/shared/CardContentPages/Opportunities'));
const SharedSales = lazy(() => import('App/pages/shared/CardContentPages/Sales'));
const SharedRelations = lazy(() => import('App/pages/shared/CardContentPages/Relations'));
const SharedDocuments = lazy(() => import('App/pages/shared/CardContentPages/Documents'));
const SharedUDO = lazy(() => import('App/pages/shared/CardContentPages/UDO'));
const ContactMarket = lazy(() => import('App/components/ContactMarket/ContactMarket'));
const ContactSupport = lazy(() => import('App/pages/ClientCard/ContentPages/Support/ClientContactCardTicketList'));
const ContactProjectPlan = lazy(() => import('App/pages/ClientCard/ContentPages/ProjectPlan/ProjectPlan'));

export const toCardEntity = (contact: Contact): CardEntity => ({
	type: 'contact',
	id: contact.id,
	data: contact,
	clientId: contact.client?.id
});

/**
 * Unlike the client card, the contact pages do not take a uniform set of props —
 * the shared pages want a `cardEntity`, the reused client-card pages want a
 * client plus a contact. So this maps a page to what it renders rather than to a
 * component, and stays the single place those prop shapes are spelled out. The
 * shapes match what the Angular tab views pass through `react-driver`, which is
 * what the flag-off fallback still renders.
 */
export const contentMap = {
	overview: (contact: Contact, client: Client) => <Overview client={client} contact={contact} isContact />,
	activities: (contact: Contact) => <SharedActivities cardEntity={toCardEntity(contact)} />,
	opportunities: (contact: Contact) => <SharedOpportunities cardEntity={toCardEntity(contact)} />,
	sales: (contact: Contact) => <SharedSales cardEntity={toCardEntity(contact)} />,
	relations: (contact: Contact) => <SharedRelations cardEntity={toCardEntity(contact)} />,
	documents: (contact: Contact) => <SharedDocuments cardEntity={toCardEntity(contact)} />,
	market: (contact: Contact) => <ContactMarket contact={contact} />,
	support: (contact: Contact) => <ContactSupport client={contact} isClient={false} />,
	projectPlan: (contact: Contact, client: Client) => <ContactProjectPlan contact={contact} client={client} />
};

export const renderUdoPage = (contact: Contact, typeId: UserDefinedObjectTypeId) => (
	<SharedUDO cardEntity={toCardEntity(contact)} typeId={typeId} />
);

/** Angular url segment per page, for the flag-off redirect back to `contact.*`. */
export const reactToAngularRedirectMap = {
	overview: '',
	activities: 'activities/',
	opportunities: 'opportunities/',
	sales: 'order/',
	market: 'market/',
	relations: 'relations/',
	documents: 'files/',
	support: 'support/',
	projectPlan: 'projectPlan/'
} as const;

export type PageName = keyof typeof contentMap | `udo-${number}`;

/**
 * The page the card lands on when the route carries no page param. An
 * MA-without-CRM org has no sales dashboard to show, so the market view takes
 * its place — the Angular states did exactly this by swapping the dashboard
 * template for `contactMarket.html` (`dashBoardViewHelper` in
 * `domain/contact/contact.js`).
 */
export const defaultPage = (): PageName =>
	isAvailableProduct(Product.MA) && !isAvailableProduct(Product.CRM) ? 'market' : 'overview';

/**
 * Angular `contact.*` state name → React card page, derived by inverting
 * `reactToAngularRedirectMap` rather than restated, because the two directions
 * have to agree: the Angular card redirects to a page the React card does not
 * implement, and the React card redirects it straight back, and the user is
 * stuck in a loop. A state name is its url segment without the trailing slash,
 * and the dashboard's segment is empty.
 */
const reactPageByAngularState = Object.entries(reactToAngularRedirectMap).reduce<Record<string, PageName>>(
	(map, [page, segment]) => {
		map[segment.replace(/\/$/, '') || 'dashboard'] = page as PageName;
		return map;
	},
	{}
);

/**
 * The page a `contact.*` state should open on the React card, or null for a
 * state the React card knows nothing about (which stays on the Angular card).
 * The udo states carry their type in a param instead of the state name.
 */
export const reactPageForAngularState = (stateName: string, udoId?: string | number): PageName | null => {
	if (stateName === 'udo') {
		const typeId = parseInt(String(udoId), 10);
		return Number.isNaN(typeId) ? null : (`udo-${typeId}` as PageName);
	}
	return reactPageByAngularState[stateName] ?? null;
};

Tools.contactCardDev = {
	get useAngular() {
		const ls = localStorage.getItem('contactCard.useAngular');
		if (ls === null) {
			return null;
		}
		return ls === 'true';
	},
	set useAngular(v) {
		if (v === null) {
			localStorage.removeItem('contactCard.useAngular');
			return;
		}
		localStorage.setItem('contactCard.useAngular', String(v));
	}
};

declare global {
	namespace Tools {
		let contactCardDev: {
			get useAngular(): boolean | null;
			set useAngular(v: boolean | null);
		};
	}
}

export const shouldUseAngularPage = (pageName: PageName) => {
	if (!hasSoftDeployAccess('REACT_CONTACT_CARD')) {
		return true;
	}

	if (config.ENV === 'DEV' && Tools.contactCardDev.useAngular !== null) {
		return Tools.contactCardDev.useAngular;
	}

	if (typeof pageName === 'string' && pageName.startsWith('udo-')) {
		return false;
	}

	return !contentMap.hasOwnProperty(pageName);
};
